import { setGlobalData } from '@/actions/global-data';
import ApiErrorBanner from '@/components/ApiErrorBanner';
import Loading from '@/components/Loading';
import Header from '@/components/UI/Header';
import { Panel, Tab, Tabs } from '@/components/UI/Tabs';
import { _isNil, _notNil } from '@/littledash';
import cn from 'classnames';
import { type ID } from '@/model/Common.model';
import type { MetadataFieldTypes } from '@/model/Metadata.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import Http from '@/support/http';
import { api as apiRoutes } from '@/support/route';
import { structuredCloneUtil } from '@/utils/browser.utils';
import { createSelector } from '@reduxjs/toolkit';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  createInitialDataLookup,
  encodeMetadata,
  filterMetadataTypesByFeature,
  sortMetadataByActive,
  validateExtendedMetadataItem,
} from './IndividualSaveMetadata.utils';
import IndividualSaveRepeater from './Repeater/IndividualSaveRepeater';
import { type Errors, type ExtendedMetadataItem } from './Repeater/IndividualSaveRepeater.utils';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError.ts';
import { notAborted } from '@/support/Hooks/fetch/useAbortController';
import { RouteService } from '@/support/RouteService';

interface MetadataGlossaryItem {
  id: number;
  api_id?: string;
  type: string;
  header?: string;
  title: string;
  field_type: MetadataFieldTypes;
  active?: boolean;
  in_use?: boolean;
  options?: Array<string>;
  read_only: boolean;
  slug: string;
  created_at?: string;
  required?: boolean;
}

type MetadataGlossaryResponse = MetadataGlossaryItem[];

interface MetadataHooks {
  [key: string]: ReturnType<typeof useApiHook<'GET /api/v1/metadata-glossary'>>;
}

interface TeamState {
  team: {
    features: {
      disease_induction: boolean;
    };
  };
}

const IndividualSaveMetadata: FC = () => {
  const [initialData, setInitialData] = useState<Record<string, Array<ExtendedMetadataItem>>>({});
  const [tabData, setTabData] = useState<Record<string, Array<ExtendedMetadataItem>>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [formErrors, setFormErrors] = useState<Array<Errors>>([]);
  const [submissionError, setSubmissionError] = useState<false | Error>(false);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [loadedTabs, setLoadedTabs] = useState<Set<string>>(new Set());
  const [errorTabs, setErrorTabs] = useState<Set<string>>(new Set());

  const selectDiseaseInduction = createSelector(
    (state: TeamState) => state.team.features.disease_induction,
    (disease_induction) => ({ disease_induction })
  );

  const { disease_induction } = useSelector(selectDiseaseInduction);

  const filteredMetadataTypes = filterMetadataTypesByFeature(disease_induction);

  const activeTabKey = filteredMetadataTypes[activeTabIndex]?.key || '';

  const currentTabData = useMemo(() => tabData[activeTabKey] ?? [], [tabData, activeTabKey]);
  const currentInitialData = useMemo(() => initialData[activeTabKey] ?? [], [initialData, activeTabKey]);
  const initialDataLookup = useMemo(() => createInitialDataLookup(currentInitialData), [currentInitialData]);

  const dispatch = useDispatch();

  const studyMetadata = useApiHook({
    endpoint: 'GET /api/v1/metadata-glossary',
    query: {
      include_inactive: true,
      filter_type: 'study_meta',
    },
    invokeOnInit: false,
    options: { onError: { throw: true, capture: false, toast: true } },
  });

  const groupMetadata = useApiHook({
    endpoint: 'GET /api/v1/metadata-glossary',
    query: {
      include_inactive: true,
      filter_type: 'group_meta',
    },
    invokeOnInit: false,
    options: { onError: { throw: true, capture: false, toast: true } },
  });

  const animalMetadata = useApiHook({
    endpoint: 'GET /api/v1/metadata-glossary',
    query: {
      include_inactive: true,
      filter_type: 'subject_meta',
    },
    invokeOnInit: false,
    options: { onError: { throw: true, capture: false, toast: true } },
  });

  const cageMetadata = useApiHook({
    endpoint: 'GET /api/v1/metadata-glossary',
    query: {
      include_inactive: true,
      filter_type: 'collection_meta',
    },
    invokeOnInit: false,
    options: { onError: { throw: true, capture: false, toast: true } },
  });

  const sampleMetadata = useApiHook({
    endpoint: 'GET /api/v1/metadata-glossary',
    query: {
      include_inactive: true,
      filter_type: 'sample_meta',
    },
    invokeOnInit: false,
    options: { onError: { throw: true, capture: false, toast: true } },
  });

  const attachmentMetadata = useApiHook({
    endpoint: 'GET /api/v1/metadata-glossary',
    query: {
      include_inactive: true,
      filter_type: 'attachment_meta',
    },
    invokeOnInit: false,
    options: { onError: { throw: true, capture: false, toast: true } },
  });

  const treatmentMetadata = useApiHook({
    endpoint: 'GET /api/v1/metadata-glossary',
    query: {
      include_inactive: true,
      filter_type: 'treatment_meta',
    },
    invokeOnInit: false,
    options: { onError: { throw: true, capture: false, toast: true } },
  });

  const diseaseMetadata = useApiHook({
    endpoint: 'GET /api/v1/metadata-glossary',
    query: {
      include_inactive: true,
      filter_type: 'disease_meta',
    },
    invokeOnInit: false,
    options: { onError: { throw: true, capture: false, toast: true } },
  });

  const metadataHooks: MetadataHooks = {
    study_meta: studyMetadata,
    group_meta: groupMetadata,
    subject_meta: animalMetadata,
    collection_meta: cageMetadata,
    sample_meta: sampleMetadata,
    attachment_meta: attachmentMetadata,
    treatment_meta: treatmentMetadata,
    disease_meta: diseaseMetadata,
  };

  const fetchMetadataForTab = async (tabKey: string): Promise<void> => {
    const hook = metadataHooks[tabKey];
    if (_notNil(hook) && !loadedTabs.has(tabKey)) {
      setLoading(true);
      try {
        await hook.invoke();
        // Clear any previous error for this tab on successful load
        setErrorTabs((prev) => {
          const newSet = new Set(prev);
          newSet.delete(tabKey);
          return newSet;
        });
        // Only mark as loaded on successful API call
        setLoadedTabs((prev) => {
          const newSet = new Set(prev);
          newSet.add(tabKey);
          return newSet;
        });
      } catch (error) {
        if (notAborted(error)) {
          ExceptionHandler.captureException(
            new InVivoError('Could not fetch metadata for tab', {
              cause: error,
              slug: 'metadata-glossary-tab',
            })
          );
          // Mark this tab as having an error (only for real API errors, not aborted requests)
          setErrorTabs((prev) => {
            const newSet = new Set(prev);
            newSet.add(tabKey);
            return newSet;
          });
        }
        // Don't mark failed tabs as loaded - this allows retries
      } finally {
        setLoading(false);
      }
    }
  };

  const getDataForActiveTab = (): MetadataGlossaryResponse => {
    if (!activeTabKey) {
      return [];
    }
    const hook = metadataHooks[activeTabKey];
    return (hook?.response?.body as MetadataGlossaryResponse) ?? [];
  };

  useEffect(() => {
    if (activeTabKey) {
      fetchMetadataForTab(activeTabKey);
    }
  }, [activeTabKey]);

  useEffect(() => {
    if (_notNil(activeTabKey) && metadataHooks[activeTabKey]?.response?.body) {
      // Only set data if this tab doesn't have data yet (preserves user changes)
      if (_isNil(tabData[activeTabKey])) {
        const rawTabData = getDataForActiveTab();
        const modifiedCollection = rawTabData.map(encodeMetadata).sort(sortMetadataByActive);

        setTabData((prev) => ({
          ...prev,
          [activeTabKey]: modifiedCollection,
        }));

        setInitialData((prev) => ({
          ...prev,
          [activeTabKey]: structuredCloneUtil(modifiedCollection),
        }));
      }
    }
  }, [activeTabKey, metadataHooks[activeTabKey]?.response, tabData]);

  const validateField = useCallback(
    async (item: ExtendedMetadataItem, data: Array<ExtendedMetadataItem>): Promise<Errors | null> => {
      const validateRow = await validateExtendedMetadataItem(item, data);
      if (validateRow) {
        return {
          index: item.index,
          id: item.id as ID,
          type: item.type || '',
          errors: validateRow,
        };
      }
      return null;
    },
    []
  );

  const validateFields = useCallback(
    async (data: Array<ExtendedMetadataItem>): Promise<void> => {
      const results: Array<Errors> = [];

      for (let index = 0; index < data.length; index++) {
        const item = data[index];
        const validationResult = await validateField(item, data);
        if (_notNil(validationResult)) {
          results.push(validationResult);
        }
      }

      setFormErrors(results);
    },
    [validateField]
  );

  useEffect(() => {
    if (currentTabData.length > 0) {
      validateFields(currentTabData);
    } else {
      setFormErrors([]);
    }
  }, [currentTabData, validateFields]);

  const updateCurrentTabData = (newData: Array<ExtendedMetadataItem>): void => {
    setTabData((prev) => ({
      ...prev,
      [activeTabKey]: newData,
    }));
  };

  const fetchGlobalData = async (): Promise<void> => {
    try {
      const response = await Http.get(apiRoutes('global-data'));
      setGlobalData(response.data, dispatch);
    } catch (error) {
      setSubmissionError(error as Error);
    }
  };
  return (
    <div
      className={`${loading ? 'ui__disabled' : ''}`}
      data-test-component="IndividualSaveMetadata"
      data-test-element="container"
    >
      <Header mainHeaderText="Metadata" />
      <div className="pb3">
        <p className="f6">
          Customise the metadata your team can select from.
          <a
            target="_blank"
            rel="noopener noreferrer"
            className="dib ml1 link blue"
            href={RouteService.help('metadata')}
          >
            Read more
          </a>
        </p>

        {submissionError && <ApiErrorBanner error={submissionError} />}
      </div>
      <Tabs outerState={[activeTabIndex, setActiveTabIndex]}>
        <div className="bb b--moon-gray">
          {filteredMetadataTypes.map(({ key, title }, index) => (
            <Tab
              key={key}
              className={cn('f5 f4-l', {
                red: formErrors.some((e) => e && e.type === key),
              })}
            >
              {title}
              {loading && activeTabIndex === index && <span className="ml2">⟳</span>}
            </Tab>
          ))}
        </div>
        {filteredMetadataTypes.map(({ key, title }, index) => (
          <Panel key={key}>
            {loading ? (
              <Loading txt={`Loading ${title} metadata...`} />
            ) : (
              <IndividualSaveRepeater
                title={title}
                data={currentTabData}
                errors={formErrors}
                type={key}
                setData={updateCurrentTabData}
                initialDataLookup={initialDataLookup}
                onRefreshGlobalData={fetchGlobalData}
                hasLoaded={loadedTabs.has(key)}
                hasError={errorTabs.has(key)}
                setFormErrors={setFormErrors}
              />
            )}
          </Panel>
        ))}
      </Tabs>
    </div>
  );
};

export default IndividualSaveMetadata;
